// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Group, GroupSearchFilters } from '../../types/groups';
import { UserRole } from '../../types/auth';
import { consoleService } from '../../services/consoleService';
import { authService } from '../../services/authService';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import ConfirmDialog from '../ConfirmDialog';
import './Groups.scss';

// SVG Icons
const RedTriangleIcon = () => (
  <img src="/static/svg/red-triangle.svg" alt="" width="16" height="16" className="mr-2" />
);

const PageFirstIcon = () => (
  <img src="/static/svg/page-first.svg" alt="First page" width="20" height="20" />
);

const PageNextIcon = () => (
  <img src="/static/svg/page-next.svg" alt="Next page" width="20" height="20" />
);

const LoadingSpinner = () => (
  <div className="spinner-border spinner-border-sm" role="status">
    <span className="visually-hidden">Loading...</span>
  </div>
);

const Groups: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { dialogState, openConfirmDialog, closeConfirmDialog, handleConfirm } = useConfirmDialog();

  const [groups, setGroups] = useState<Group[]>([]);
  const [nextCursor, setNextCursor] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchFilters, setSearchFilters] = useState<GroupSearchFilters>({
    name: '',
    lang_tag: '',
    members: undefined,
    open: undefined,
  });

  const sessionRole = authService.getSessionRole();

  // Initialize filters from URL params
  useEffect(() => {
    const name = searchParams.get('name') || '';
    const langTag = searchParams.get('lang_tag') || '';
    const members = searchParams.get('members');
    const open = searchParams.get('open');
    const cursor = searchParams.get('cursor') || '';

    setSearchFilters({
      name,
      lang_tag: langTag,
      members: members ? parseInt(members) : undefined,
      open: open ? open === 'true' : undefined,
    });
    setNextCursor(cursor);

    if (cursor || name || langTag || members || open) {
      loadGroups(cursor, {
        name,
        lang_tag: langTag,
        members: members ? parseInt(members) : undefined,
        open: open ? open === 'true' : undefined,
      });
    } else {
      loadGroups();
    }
  }, []);

  const loadGroups = useCallback(async (
    cursor?: string,
    filters?: GroupSearchFilters
  ) => {
    setLoading(true);
    setError('');

    try {
      const result = await consoleService.listGroups(
        '',
        filters || searchFilters,
        cursor
      );

      setGroups(result.groups || []);
      setNextCursor(result.cursor || '');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load groups');
    } finally {
      setLoading(false);
    }
  }, [searchFilters]);

  const handleSearch = useCallback((direction: number = 0) => {
    let cursor = '';
    switch (direction) {
      case 0:
        cursor = '';
        break;
      case 1:
        cursor = nextCursor;
        break;
    }

    // Update URL params
    const params = new URLSearchParams();
    if (searchFilters.name) params.set('name', searchFilters.name);
    if (searchFilters.lang_tag) params.set('lang_tag', searchFilters.lang_tag);
    if (searchFilters.members !== undefined) params.set('members', searchFilters.members.toString());
    if (searchFilters.open !== undefined) params.set('open', searchFilters.open.toString());
    if (cursor) params.set('cursor', cursor);
    
    setSearchParams(params);
    loadGroups(cursor, searchFilters);
  }, [searchFilters, nextCursor, setSearchParams, loadGroups]);

  const handleFilterChange = (field: keyof GroupSearchFilters, value: string | number | boolean | undefined) => {
    setSearchFilters(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSearch(0);
  };

  const handleDeleteGroup = (group: Group, index: number) => {
    openConfirmDialog(
      'Delete Group',
      `Are you sure you want to delete the group "${group.name}" (${group.id})? This action cannot be undone and will remove all group members and data.`,
      async () => {
        try {
          await consoleService.deleteGroup('', group.id);
          setGroups(prev => prev.filter((_, i) => i !== index));
          setError('');
        } catch (err: any) {
          setError(err.response?.data?.message || err.message || 'Failed to delete group');
        }
      }
    );
  };

  const handleViewGroup = (group: Group) => {
    navigate(`/groups/${group.id}`);
  };

  const deleteAllowed = () => {
    return sessionRole === UserRole.USER_ROLE_ADMIN;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString();
  };

  const formatMetadata = (metadata?: string) => {
    if (!metadata) return '';
    try {
      const parsed = JSON.parse(metadata);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return metadata;
    }
  };

  const getGroupTypeText = (open: boolean) => {
    return open ? 'Open' : 'Private';
  };

  return (
    <div className="groups-container">
      <div className="groups-header">
        <h2 className="groups-title">Groups</h2>
        <h6 className="groups-subtitle">{groups.length} groups found.</h6>
      </div>

      <div className="groups-controls">
        <div className="search-section">
          <form onSubmit={handleSubmit} className="search-form">
            <div className="search-input-group">
              <input
                type="text"
                className="search-input"
                value={searchFilters.name || ''}
                onChange={(e) => handleFilterChange('name', e.target.value)}
                placeholder="Filter by group name"
              />
              <input
                type="text"
                className="lang-input"
                value={searchFilters.lang_tag || ''}
                onChange={(e) => handleFilterChange('lang_tag', e.target.value)}
                placeholder="Language tag (e.g., en, fr, de)"
              />
              <input
                type="number"
                className="members-input"
                value={searchFilters.members || ''}
                onChange={(e) => handleFilterChange('members', e.target.value ? parseInt(e.target.value) : undefined)}
                placeholder="Min members"
                min="0"
              />
              <select
                className="open-select"
                value={searchFilters.open === undefined ? '' : searchFilters.open.toString()}
                onChange={(e) => handleFilterChange('open', e.target.value === '' ? undefined : e.target.value === 'true')}
              >
                <option value="">All Types</option>
                <option value="true">Open Groups</option>
                <option value="false">Private Groups</option>
              </select>
              <div className="search-buttons">
                <button
                  type="submit"
                  className="btn btn-primary search-btn"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <div className="spinner-border spinner-border-sm me-1" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      Searching...
                    </>
                  ) : (
                    'Search'
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>

        <div className="pagination-section">
          <div className="pagination-buttons">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => handleSearch(0)}
              disabled={groups.length === 0 || loading}
              title="First page"
            >
              <PageFirstIcon />
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => handleSearch(1)}
              disabled={!nextCursor || loading}
              title="Next page"
            >
              <PageNextIcon />
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger">
          <RedTriangleIcon />
          <strong>Error when querying groups:</strong> {error}
        </div>
      )}

      {loading && (
        <div className="alert alert-info">
          <LoadingSpinner />
          <strong className="ms-2">Loading groups...</strong>
        </div>
      )}

      <div className="groups-table-container">
        <table className="groups-table">
          <thead>
            <tr>
              <th style={{ width: '200px' }}>Group Name</th>
              <th style={{ width: '320px' }}>Group ID</th>
              <th>Type</th>
              <th>Members</th>
              <th>Language</th>
              <th>Creator</th>
              <th style={{ width: '180px' }}>Created</th>
              {deleteAllowed() && <th style={{ width: '90px' }}>Remove</th>}
            </tr>
          </thead>
          <tbody>
            {groups.length === 0 && !loading && (
              <tr>
                <td colSpan={deleteAllowed() ? 8 : 7} className="no-groups">
                  No groups found - change the filter criteria or create new groups.
                </td>
              </tr>
            )}
            {groups.map((group, index) => (
              <tr key={group.id} className="group-row">
                <td onClick={() => handleViewGroup(group)} className="clickable">
                  <div className="group-name">{group.name}</div>
                  {group.description && (
                    <div className="group-description">{group.description}</div>
                  )}
                </td>
                <td onClick={() => handleViewGroup(group)} className="clickable">
                  <code className="group-id">{group.id}</code>
                </td>
                <td onClick={() => handleViewGroup(group)} className="clickable">
                  <span className={`type-badge ${group.open ? 'open' : 'private'}`}>
                    {getGroupTypeText(group.open)}
                  </span>
                </td>
                <td onClick={() => handleViewGroup(group)} className="clickable">
                  <div className="member-count">
                    <span className="current">{group.edge_count}</span>
                    {group.max_count > 0 && (
                      <>
                        <span className="separator">/</span>
                        <span className="max">{group.max_count}</span>
                      </>
                    )}
                  </div>
                </td>
                <td onClick={() => handleViewGroup(group)} className="clickable">
                  {group.lang_tag || '-'}
                </td>
                <td onClick={() => handleViewGroup(group)} className="clickable">
                  <code className="creator-id">{group.creator_id}</code>
                </td>
                <td onClick={() => handleViewGroup(group)} className="clickable">
                  {formatDate(group.create_time)}
                </td>
                {deleteAllowed() && (
                  <td className="delete-cell">
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDeleteGroup(group, index)}
                    >
                      Delete
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        isOpen={dialogState.isOpen}
        title={dialogState.title}
        message={dialogState.message}
        onConfirm={handleConfirm}
        onCancel={closeConfirmDialog}
      />
    </div>
  );
};

export default Groups;
